import React, {useEffect, useState} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {SingleDatePicker} from 'react-dates';
import moment, {Moment} from 'moment';
import useTranslate from '../../../core/languages/hooks/useTranslate';
import useCurrentLanguage from '../../../core/languages/hooks/useCurrentLanguage';
import useNcId from '../../../ui/hooks/useNcId';
import { getTransportDateLabel } from '../../utils/transportLabels';
import Label from '../../../ui/components/Label/Label';
import InputError from '../../../ui/shared/InputError/InputError';
import EventCity from "../../../eventCities/types/EventCity";

interface TransportDateProps {
    name: string;
    transportFieldName: string;
    direction: 'arrival' | 'departure';
    eventCity?: EventCity;
}

const TransportDate: React.FC<TransportDateProps> = ({ name, transportFieldName, direction, eventCity }) => {
    const t = useTranslate();
    const languageCode = useCurrentLanguage();
    const { control, formState: { errors }, watch } = useFormContext();
    const transport = watch(transportFieldName);
    const dateValue = watch(name);

    const [focusedInput, setFocusedInput] = useState<boolean>(false);
    const [date, setDate] = useState<Moment | null>(dateValue ? moment(dateValue) : null);
    const dateId = useNcId();

    useEffect(() => {
        setDate(dateValue ? moment(dateValue) : null);
    }, [dateValue]);

    const getFormattedDate = (): string => {
        if (!date) return '';
        // Apply current locale on the instance so it respects the active language
        return date.locale(languageCode).format("DD/MM/YYYY");
    };

    const hasError = !!(errors as any)[name];
    const borderClass = hasError 
        ? "border-red-400 focus:border-red-500 dark:border-red-800" 
        : "border-neutral-200 dark:border-neutral-700";

    return (
        <Controller
            name={name}
            control={control}
            rules={{ required: t("common.feedback.field_required") }}
            render={({ field }) => (
                <div className="space-y-1 relative">
                    <Label isInvalid={hasError}>
                        {getTransportDateLabel(t, transport, direction)}
                    </Label>
                    <div className="relative">
                        <input
                            type="text"
                            readOnly
                            onClick={() => setFocusedInput(true)}
                            className={`block w-full h-11 px-4 py-3 border rounded-2xl text-sm font-normal
                            bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none
                            focus:ring focus:ring-opacity-50 dark:focus:ring-opacity-25 cursor-pointer
                            ${hasError ? 'focus:ring-red-200 dark:focus:ring-red-900' : 'focus:ring-primary-200 dark:focus:ring-primary-6000'} ${borderClass}`}
                            placeholder="DD/MM/YYYY"
                            value={getFormattedDate()}
                        />
                        
                        {/* Calendar positioned below input */}
                        {focusedInput && (
                            <div className="absolute top-2 left-0 z-50">
                                <SingleDatePicker
                                    date={date}
                                    onDateChange={(newDate) => {
                                        setDate(newDate);
                                        field.onChange(newDate ? newDate.format("YYYY-MM-DD") : "");
                                        setFocusedInput(false);
                                    }}
                                    focused={focusedInput}
                                    onFocusChange={(focus: any) => setFocusedInput(focus.focused)}
                                    id={dateId}
                                    numberOfMonths={1}
                                    daySize={40}
                                    noBorder
                                    hideKeyboardShortcutsPanel
                                    reopenPickerOnClearDate
                                    isOutsideRange={(day) => {
                                        const today = moment().startOf('day');
                                        if (day.isBefore(today, 'day')) {
                                            return true;
                                        }

                                        const startDate = eventCity?.transferStartDate || eventCity?.startDate;
                                        const endDate = eventCity?.transferEndDate || eventCity?.endDate;

                                        if (!startDate || !endDate) return false;

                                        const start = moment(startDate);
                                        const end = moment(endDate);
                                        return day.isBefore(start, 'day') || day.isAfter(end, 'day');
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    {hasError && <InputError>{(errors as any)[name]?.message}</InputError>}
                </div>
            )}
        />
    );
};

export default TransportDate;
